import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress, Container, Alert } from '@mui/material';
import { RootState } from '../../store';
import { getCurrentAuthContext } from '../../services/auth';
import { logout } from '../../features/auth/authSlice';
import { usePermissions, PERMISSIONS, type PermissionKey } from '../../permissions';

// Rota protegida genérica
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Rota de admin de paróquia
export const ParishAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, user, loading } = useSelector((state: RootState) => state.auth);
  const authContext = getCurrentAuthContext();

  // Função para fazer logout automático
  const handleAutoLogout = React.useCallback(() => {
    console.log('🚪 ParishAdminRoute - Erro de autenticação detectado, fazendo logout automático...');
    dispatch(logout());
    // Limpar localStorage adicional
    localStorage.removeItem('token_parish');
    localStorage.removeItem('token_admin');
    localStorage.removeItem('user_parish');
    localStorage.removeItem('user_admin');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('✅ ParishAdminRoute - Logout automático concluído');
  }, [dispatch]);

  // Effect para logout automático quando há problemas de autenticação
  React.useEffect(() => {
    if (!loading && (user === null || user === undefined || !user?.userType)) {
      console.log('🚨 ParishAdminRoute - Problema de autenticação detectado, fazendo logout automático');
      handleAutoLogout();
    }
  }, [loading, user, handleAutoLogout]);

  console.log('🔍 ParishAdminRoute - Estado:', { 
    isAuthenticated, 
    user: user?.userType, 
    loading, 
    authContext 
  });

  if (!isAuthenticated) {
    console.log('❌ Não autenticado, redirecionando para login');
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    console.log('⏳ Loading, mostrando spinner');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Se user é null ou undefined após loading, mostrar spinner (logout será feito pelo effect)
  if (user === null || user === undefined) {
    console.log('🚨 ParishAdminRoute - User null/undefined após loading, aguardando logout automático');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Verificar se userType existe antes de acessá-lo
  if (!user.userType) {
    console.log('🚨 ParishAdminRoute - userType não existe, aguardando logout automático');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Permitir acesso se estiver no contexto de paróquia OU se for parish_admin
  if (authContext !== 'parish' && user.userType !== 'parish_admin') {
    console.log('❌ Usuário não autorizado para contexto parish:', { 
      authContext, 
      userType: user.userType 
    });
    return <Navigate to="/" replace />;
  }

  console.log('✅ Usuário autorizado para contexto parish, renderizando children');
  return <>{children}</>;
};

/** Mensagens quando o usuário não tem a permissão */
const PERMISSION_MESSAGES: Partial<Record<string, string>> = {
  [PERMISSIONS.MANAGE_SUBPARISHES]:
    'Apenas organizações autorizadas podem ver e gerenciar setores. Se precisar dessa funcionalidade, solicite a liberação ao administrador da plataforma.',
  [PERMISSIONS.REQUEST_EARLY_WITHDRAWAL]:
    'Sua paróquia não está autorizada a solicitar saque antecipado. Entre em contato com o administrador da plataforma.',
};

/**
 * Protege uma rota por permissão. Deve ser usado dentro de ParishAdminRoute.
 * Se o usuário não tiver a permissão, exibe uma mensagem em vez do conteúdo.
 */
export const RequirePermission: React.FC<{
  permission: PermissionKey;
  children: React.ReactNode;
}> = ({ permission, children }) => {
  const permissions = usePermissions();
  const permValue = PERMISSIONS[permission];
  const hasPermission = !!permissions[permValue];
  const message = PERMISSION_MESSAGES[permValue] ?? 'Você não tem permissão para acessar esta área.';

  if (!hasPermission) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="info">{message}</Alert>
      </Container>
    );
  }

  return <>{children}</>;
};

// Rota de super admin
export const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, user, loading } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  // Função para fazer logout automático
  const handleAutoLogout = React.useCallback(() => {
    console.log('🚪 SuperAdminRoute - Erro de autenticação detectado, fazendo logout automático...');
    dispatch(logout());
    // Limpar localStorage adicional
    localStorage.removeItem('token_parish');
    localStorage.removeItem('token_admin');
    localStorage.removeItem('user_parish');
    localStorage.removeItem('user_admin');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('✅ SuperAdminRoute - Logout automático concluído');
  }, [dispatch]);

  // Effect para logout automático quando há problemas de autenticação
  React.useEffect(() => {
    if (!loading && (user === null || user === undefined || !user?.userType)) {
      console.log('🚨 SuperAdminRoute - Problema de autenticação detectado, fazendo logout automático');
      handleAutoLogout();
    }
  }, [loading, user, handleAutoLogout]);

  console.log('🔍 SuperAdminRoute - Estado:', { 
    isAuthenticated, 
    user: user?.userType, 
    loading, 
    userIsNull: user === null,
    userIsUndefined: user === undefined
  });

  if (!isAuthenticated) {
    const loginPath = location.pathname.includes('invoice-issuance') ? '/contador' : '/login';
    console.log('❌ SuperAdminRoute - Não autenticado, redirecionando para', loginPath);
    return <Navigate to={loginPath} replace />;
  }

  if (loading) {
    console.log('⏳ SuperAdminRoute - Loading, mostrando spinner');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Se user é null ou undefined após loading, mostrar spinner (logout será feito pelo effect)
  if (user === null || user === undefined) {
    console.log('🚨 SuperAdminRoute - User null/undefined após loading, aguardando logout automático');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Verificar se userType existe antes de acessá-lo
  if (!user.userType) {
    console.log('🚨 SuperAdminRoute - userType não existe, aguardando logout automático');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user.userType !== 'super_admin' && user.userType !== 'contador') {
    console.log('❌ SuperAdminRoute - Usuário não autorizado no admin:', user.userType);
    return <Navigate to="/" replace />;
  }

  console.log('✅ SuperAdminRoute - Usuário autorizado, renderizando children');
  return <>{children}</>;
};
